import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { ToastProvider as EnhancedToastProvider } from './contexts/EnhancedToastContext';
import { supabase } from './lib/supabase';
import Layout from './components/Layout';
import MarketingLayout from './components/MarketingLayout';
import FeatureProtectedRoute from './components/FeatureProtectedRoute';

// Lazy load pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const OrganizationSetup = lazy(() => import('./pages/OrganizationSetup'));
const Centers = lazy(() => import('./pages/Centers'));
const Classes = lazy(() => import('./pages/Classes'));
const Coaches = lazy(() => import('./pages/Coaches'));
const UpcomingClasses = lazy(() => import('./pages/UpcomingClasses'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const StudentDashboard = lazy(() => import('./pages/StudentDashboard'));
const Login = lazy(() => import('./pages/Login'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Messages = lazy(() => import('./pages/Messages'));
const Payments = lazy(() => import('./pages/Payments'));
const OneTimePayments = lazy(() => import('./pages/OneTimePayments'));
const Profile = lazy(() => import('./pages/Profile'));
const Register = lazy(() => import('./pages/Register'));
const ParentRegistration = lazy(() => import('./pages/ParentRegistration'));
const OrganizationRegistration = lazy(() => import('./pages/OrganizationRegistration'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const BusinessTerms = lazy(() => import('./pages/BusinessTerms'));
const Students = lazy(() => import('./pages/Students'));
const Parents = lazy(() => import('./pages/Parents'));
const Reports = lazy(() => import('./pages/Reports'));
const TournamentSelection = lazy(() => import('./pages/TournamentSelection'));
const ContactList = lazy(() => import('./pages/ContactList'));
const CenterStatistics = lazy(() => import('./pages/CenterStatistics'));
const Syllabus = lazy(() => import('./pages/Syllabus'));
const BeltLevels = lazy(() => import('./pages/BeltLevels'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const SuperAdminUsers = lazy(() => import('./pages/SuperAdminUsers'));
const SuperAdminOrgUsers = lazy(() => import('./pages/SuperAdminOrgUsers'));
const OrganizationConversion = lazy(() => import('./pages/OrganizationConversion'));
const SuperAdminFinancialDashboard = lazy(() => import('./pages/SuperAdminFinancialDashboard'));
const SystemSettings = lazy(() => import('./pages/SystemSettings'));
const OrganizationBillingPage = lazy(() => import('./pages/OrganizationBillingPage'));
const OverdueCustomersReport = lazy(() => import('./pages/OverdueCustomersReport'));
const PricingConfiguration = lazy(() => import('./pages/PricingConfiguration'));
const CreateOrganization = lazy(() => import('./pages/CreateOrganization'));
const EditOrganization = lazy(() => import('./pages/EditOrganization'));
const ManagerAssignment = lazy(() => import('./pages/ManagerAssignment'));
const OrganizationSettings = lazy(() => import('./pages/OrganizationSettings'));
const AdvancedReportsPage = lazy(() => import('./pages/AdvancedReportsPage'));
const DataExportPage = lazy(() => import('./pages/DataExportPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const NotificationPreferencesPage = lazy(() => import('./pages/NotificationPreferencesPage'));
const MarketingCMS = lazy(() => import('./pages/MarketingCMS'));
const DatabaseHelper = lazy(() => import('./pages/DatabaseHelper'));

// Documentation pages
const DocumentationHome = lazy(() => import('./pages/documentation/DocumentationHome'));
const SystemOverviewDoc = lazy(() => import('./pages/documentation/SystemOverviewDoc'));
const TestingGuideDoc = lazy(() => import('./pages/documentation/TestingGuideDoc'));
const TestRecordingDoc = lazy(() => import('./pages/documentation/TestRecordingDoc'));
const QuickReferenceDoc = lazy(() => import('./pages/documentation/QuickReferenceDoc'));
const RegistrationGuideDoc = lazy(() => import('./pages/documentation/RegistrationGuideDoc'));
const OrganizationOwnerGuideDoc = lazy(() => import('./pages/documentation/OrganizationOwnerGuideDoc'));
const CenterManagerGuideDoc = lazy(() => import('./pages/documentation/CenterManagerGuideDoc'));
const CoachGuideDoc = lazy(() => import('./pages/documentation/CoachGuideDoc'));
const ParentGuideDoc = lazy(() => import('./pages/documentation/ParentGuideDoc'));

// Attendance components
const MarkAttendance = lazy(() => import('./pages/MarkAttendance'));
const AttendanceReport = lazy(() => import('./pages/AttendanceReport'));

// Student Progress component
const StudentProgress = lazy(() => import('./pages/StudentProgress'));

// Special Classes components
const SpecialClassManager = lazy(() => import('./components/SpecialClasses/SpecialClassManager'));
const ParentSpecialClassesView = lazy(() => import('./components/SpecialClasses/ParentSpecialClassesView'));
const SpecialClassCalendar = lazy(() => import('./components/SpecialClasses/SpecialClassCalendar'));

// Tournament components
const TournamentManagement = lazy(() => import('./pages/TournamentManagement'));
const TournamentEventMaster = lazy(() => import('./pages/TournamentEventMaster'));
const TournamentEventSelection = lazy(() => import('./pages/TournamentEventSelection'));
const ParentTournaments = lazy(() => import('./pages/ParentTournaments'));
const TournamentRegistrationFlow = lazy(() => import('./pages/TournamentRegistrationFlow'));
const ManagementRegistrationReview = lazy(() => import('./pages/ManagementRegistrationReview'));

// Shared Components
const OfflineIndicator = lazy(() => import('./components/OfflineIndicator'));
const InstallPrompt = lazy(() => import('./components/InstallPrompt'));
const NotificationPreferences = lazy(() => import('./components/NotificationPreferences'));
const TokenManagement = lazy(() => import('./components/TokenManagement'));

// Smart Redirect component
const SmartRedirect = () => {
  const { isSuperAdmin, organization, user, profile } = useAuth();
  const [isStudentUser, setIsStudentUser] = useState(false);
  const [localLoading, setLocalLoading] = useState(true);

  useEffect(() => {
    const checkIfStudent = async () => {
      if (!user) {
        setLocalLoading(false);
        return;
      }

      // Skip student check if we already have profile data indicating non-student role
      if (profile && profile.role && profile.role !== 'student') {
        setIsStudentUser(false);
        setLocalLoading(false);
        return;
      }

      try {
        const { data: studentCheck, error } = await supabase
          .from('students')
          .select('id')
          .eq('auth_user_id', user.id)
          .single();

        if (error) {
          // Handle 406 Not Acceptable errors gracefully (RLS policy issues)
          if (error.code === 'PGRST116' || error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
            console.warn('Student check query not acceptable, possibly due to RLS policies. Assuming not a student.', error);
            setIsStudentUser(false);
          } else {
            console.warn('Error checking if user is student:', error);
            setIsStudentUser(false);
          }
        } else {
          setIsStudentUser(!!studentCheck);
        }
      } catch (error) {
        console.warn('Exception during student check:', error);
        setIsStudentUser(false);
      } finally {
        setLocalLoading(false);
      }
    };

    checkIfStudent();
  }, [user, profile]);

  if (localLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isSuperAdmin() && !organization) {
    return <Navigate to="/super-admin" />;
  }

  if (isStudentUser) {
    return <Navigate to="/student-dashboard" />;
  }

  return <Navigate to="/app/dashboard" />;
};

// Super Admin Route component
const SuperAdminRoute = ({ children }) => {
  const { user, isSuperAdmin } = useAuth();

  if (!user) return <Navigate to="/login" />;

  if (!isSuperAdmin()) return <Navigate to="/app/dashboard" />;

  return children;
};

// Protected Route component
const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { user, profile } = useAuth();

  // Check if we're in password reset mode - if so, redirect to reset password page
  const isPasswordResetMode = sessionStorage.getItem('isPasswordResetMode') === 'true';
  if (isPasswordResetMode) {
    return <Navigate to="/reset-password" />;
  }

  if (!user) return <Navigate to="/login" />;

  if (requiredRoles.length > 0 && !requiredRoles.includes(profile?.role)) {
    return <Navigate to="/app/dashboard" />;
  }

  return children;
};

// Public Route component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, isSuperAdmin, organization } = useAuth();

  // Check if we're in password reset mode - if so, redirect to reset password page  
  const isPasswordResetMode = sessionStorage.getItem('isPasswordResetMode') === 'true';
  if (isPasswordResetMode) {
    return <Navigate to="/reset-password" />;
  }

  // Check if URL contains password reset tokens (from email link)
  const hasResetTokens = () => {
    const hash = window.location.hash;
    return hash.includes('access_token') && (hash.includes('type=recovery') || hash.includes('type=invite'));
  };

  // If user has reset tokens in URL, let them through to reset password page
  if (hasResetTokens()) {
    return children;
  }

  if (user) {
    // Redirect SuperAdmin to their dashboard
    if (isSuperAdmin() && !organization) {
      return <Navigate to="/super-admin" />;
    }
    // Regular users go to dashboard
    return <Navigate to="/app/dashboard" />;
  }

  return children;
};

// Setup Route component (for organization setup)
const SetupRoute = ({ children }) => {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" />;

  return children;
};

// Reset Password Route component (allows both logged-in and logged-out users)
const ResetPasswordRoute = ({ children }) => {
  // Always allow access to reset password page regardless of auth status
  return children;
};

// Landing page wrapper that redirects to reset password if tokens are present
const LandingPageWrapper = () => {
  const hasResetTokens = () => {
    const hash = window.location.hash;
    return hash.includes('access_token') && (hash.includes('type=recovery') || hash.includes('type=invite'));
  };

  if (hasResetTokens()) {
    return <Navigate to="/reset-password" />;
  }

  return (
    <MarketingLayout>
      <LandingPage />
    </MarketingLayout>
  );
};

function AppRoutes() {
  return (
    <Router>
      <Routes>
        {/* Marketing Landing Page */}
        <Route
          path="/"
          element={<LandingPageWrapper />}
        />

        {/* Public Routes */}
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPasswordRoute><ResetPassword /></ResetPasswordRoute>} />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route
          path="/register/parent"
          element={
            <PublicRoute>
              <ParentRegistration />
            </PublicRoute>
          }
        />
        <Route
          path="/register/organization"
          element={
            <PublicRoute>
              <OrganizationRegistration />
            </PublicRoute>
          }
        />
        <Route
          path="/pricing"
          element={
            <PublicRoute>
              <PricingPage />
            </PublicRoute>
          }
        />
        <Route
          path="/terms"
          element={
            <PublicRoute>
              <TermsOfService />
            </PublicRoute>
          }
        />
        <Route
          path="/privacy"
          element={
            <PublicRoute>
              <PrivacyPolicy />
            </PublicRoute>
          }
        />
        <Route
          path="/business-terms"
          element={
            <PublicRoute>
              <BusinessTerms />
            </PublicRoute>
          }
        />
        <Route
          path="/setup"
          element={
            <SetupRoute>
              <OrganizationSetup />
            </SetupRoute>
          }
        />

        {/* Super Admin Routes */}
        <Route
          path="/super-admin"
          element={
            <SuperAdminRoute>
              <Layout />
            </SuperAdminRoute>
          }
        >
          <Route index element={<SuperAdminDashboard />} />
          <Route path="users" element={<SuperAdminUsers />} />
          <Route path="financial" element={<SuperAdminFinancialDashboard />} />
          <Route path="settings" element={<SystemSettings />} />
          <Route path="organization-billing/:organizationId" element={<OrganizationBillingPage />} />
          <Route path="overdue-customers" element={<OverdueCustomersReport />} />
          <Route path="pricing-config" element={<PricingConfiguration />} />
          <Route path="marketing-cms" element={<MarketingCMS />} />
          <Route path="profile" element={<Profile />} />
          <Route path="notification-preferences" element={<NotificationPreferences />} />
        </Route>

        <Route
          path="/super-admin/create-organization"
          element={
            <SuperAdminRoute>
              <CreateOrganization />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/super-admin/organizations/:id/edit"
          element={
            <SuperAdminRoute>
              <EditOrganization />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/super-admin/organizations/:orgId/users"
          element={
            <SuperAdminRoute>
              <SuperAdminOrgUsers />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/super-admin/organizations/:id/convert"
          element={
            <SuperAdminRoute>
              <OrganizationConversion />
            </SuperAdminRoute>
          }
        />

        {/* Student Dashboard Route (separate from Layout) */}
        <Route
          path="/student-dashboard"
          element={
            <ProtectedRoute>
              <StudentDashboard />
            </ProtectedRoute>
          }
        />

        {/* Regular App Routes */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="db-helper" element={<DatabaseHelper />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="students" element={<FeatureProtectedRoute requiredFeature="student_management" featureName="Student Management"><Students /></FeatureProtectedRoute>} />
          <Route path="parents" element={<FeatureProtectedRoute requiredFeature="parent_management" featureName="Parent Management"><Parents /></FeatureProtectedRoute>} />
          <Route path="reports" element={<FeatureProtectedRoute requiredFeature="reports_analytics" featureName="Reports & Analytics"><Reports /></FeatureProtectedRoute>} />
          <Route path="tournament-selection" element={<FeatureProtectedRoute requiredFeature="tournament_selection" featureName="Tournament Selection"><TournamentSelection /></FeatureProtectedRoute>} />
          <Route path="contact-list" element={<FeatureProtectedRoute requiredFeature="contact_lists" featureName="Contact Lists"><ContactList /></FeatureProtectedRoute>} />
          <Route path="center-statistics" element={<FeatureProtectedRoute requiredFeature="center_statistics" featureName="Center Statistics"><CenterStatistics /></FeatureProtectedRoute>} />
          <Route path="centers" element={<FeatureProtectedRoute requiredFeature="center_management" featureName="Center Management"><Centers /></FeatureProtectedRoute>} />
          <Route path="classes" element={<FeatureProtectedRoute requiredFeature="class_management" featureName="Class Management"><Classes /></FeatureProtectedRoute>} />
          <Route path="coaches" element={<FeatureProtectedRoute requiredFeature="coach_management" featureName="Coach Management"><Coaches /></FeatureProtectedRoute>} />
          <Route path="managers" element={<FeatureProtectedRoute requiredFeature="center_management" featureName="Center Management"><ManagerAssignment /></FeatureProtectedRoute>} />
          <Route path="organization-settings" element={<OrganizationSettings />} />
          <Route path="token-management" element={<FeatureProtectedRoute requiredFeature="registration_tokens" featureName="Registration Tokens"><TokenManagement /></FeatureProtectedRoute>} />
          <Route path="upcoming-classes" element={<FeatureProtectedRoute requiredFeature="class_management" featureName="Class Management"><UpcomingClasses /></FeatureProtectedRoute>} />
          <Route path="advanced-reports" element={<FeatureProtectedRoute requiredFeature="reports_analytics" featureName="Reports & Analytics"><AdvancedReportsPage /></FeatureProtectedRoute>} />

          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="notification-settings" element={<NotificationPreferencesPage />} />
          <Route path="payments" element={<FeatureProtectedRoute requiredFeature="monthly_payments" featureName="Monthly Payments"><Payments /></FeatureProtectedRoute>} />
          <Route path="one-time-payments" element={<FeatureProtectedRoute requiredFeature="one_time_payments" featureName="One-Time Payments"><OneTimePayments /></FeatureProtectedRoute>} />
          <Route path="messages" element={<FeatureProtectedRoute requiredFeature="internal_messaging" featureName="Internal Messaging"><Messages /></FeatureProtectedRoute>} />
          <Route path="syllabus" element={<FeatureProtectedRoute requiredFeature="syllabus_management" featureName="Syllabus Management"><Syllabus /></FeatureProtectedRoute>} />
          <Route path="belt-levels" element={<FeatureProtectedRoute requiredFeature="belt_progression" featureName="Belt Progression"><BeltLevels /></FeatureProtectedRoute>} />
          <Route path="mark-attendance" element={<FeatureProtectedRoute requiredFeature="attendance_system" featureName="Attendance System"><MarkAttendance /></FeatureProtectedRoute>} />
          <Route path="attendance-report" element={<FeatureProtectedRoute requiredFeature="attendance_system" featureName="Attendance System"><AttendanceReport /></FeatureProtectedRoute>} />
          <Route path="student-progress" element={<FeatureProtectedRoute requiredFeature="student_progress" featureName="Student Progress"><StudentProgress /></FeatureProtectedRoute>} />
          <Route path="special-classes" element={<FeatureProtectedRoute requiredFeature="special_classes" featureName="Special Classes"><SpecialClassManager /></FeatureProtectedRoute>} />
          <Route path="special-classes/calendar" element={<FeatureProtectedRoute requiredFeature="special_classes" featureName="Special Classes"><SpecialClassCalendar /></FeatureProtectedRoute>} />
          <Route path="special-classes/parent" element={<FeatureProtectedRoute requiredFeature="special_classes" featureName="Special Classes"><ParentSpecialClassesView /></FeatureProtectedRoute>} />

          {/* Tournament Routes */}
          <Route path="tournaments" element={<TournamentManagement />} />
          <Route path="tournament-events" element={<TournamentEventMaster />} />
          <Route path="tournaments/:tournamentId/manage" element={<TournamentEventSelection />} />
          <Route path="tournaments/:tournamentId/registrations" element={<ManagementRegistrationReview />} />

          {/* Parent/Student Tournament Routes (with sidebar) */}
          <Route path="tournament-browse" element={<ParentTournaments />} />
          <Route path="tournament-browse/:tournamentId/register" element={<TournamentRegistrationFlow />} />

          <Route path="profile" element={<Profile />} />
          <Route path="notification-preferences" element={<NotificationPreferences />} />
          <Route index element={<SmartRedirect />} />
        </Route>

        {/* Redirect Routes for Backward Compatibility */}
        <Route path="/centers" element={<Navigate to="/app/centers" replace />} />
        <Route path="/managers" element={<Navigate to="/app/managers" replace />} />
        <Route path="/classes" element={<Navigate to="/app/classes" replace />} />
        <Route path="/coaches" element={<Navigate to="/app/coaches" replace />} />
        <Route path="/students" element={<Navigate to="/app/students" replace />} />
        <Route path="/parents" element={<Navigate to="/app/parents" replace />} />
        <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
        <Route path="/payments" element={<Navigate to="/app/payments" replace />} />
        <Route path="/one-time-payments" element={<Navigate to="/app/one-time-payments" replace />} />
        <Route path="/messages" element={<Navigate to="/app/messages" replace />} />
        <Route path="/syllabus" element={<Navigate to="/app/syllabus" replace />} />
        <Route path="/belt-levels" element={<Navigate to="/app/belt-levels" replace />} />
        <Route path="/mark-attendance" element={<Navigate to="/app/mark-attendance" replace />} />
        <Route path="/attendance-report" element={<Navigate to="/app/attendance-report" replace />} />
        <Route path="/student-progress" element={<Navigate to="/app/student-progress" replace />} />
        <Route path="/special-classes" element={<Navigate to="/app/special-classes" replace />} />
        <Route path="/special-classes/calendar" element={<Navigate to="/app/special-classes/calendar" replace />} />
        <Route path="/special-classes/parent" element={<Navigate to="/app/special-classes/parent" replace />} />
        <Route path="/upcoming-classes" element={<Navigate to="/app/upcoming-classes" replace />} />
        <Route path="/tournament-selection" element={<Navigate to="/app/tournament-selection" replace />} />
        <Route path="/contact-list" element={<Navigate to="/app/contact-list" replace />} />
        <Route path="/center-statistics" element={<Navigate to="/app/center-statistics" replace />} />
        <Route path="/token-management" element={<Navigate to="/app/token-management" replace />} />
        <Route path="/organization-settings" element={<Navigate to="/app/organization-settings" replace />} />

        {/* Documentation Routes - Publicly accessible */}
        <Route path="/guide" element={<DocumentationHome />} />
        <Route path="/guide/system-overview" element={<SystemOverviewDoc />} />
        <Route path="/guide/testing-guide" element={<TestingGuideDoc />} />
        <Route path="/guide/test-recording" element={<TestRecordingDoc />} />
        <Route path="/guide/quick-reference" element={<QuickReferenceDoc />} />
        <Route path="/guide/registration-guide" element={<RegistrationGuideDoc />} />
        <Route path="/guide/organization-owner" element={<OrganizationOwnerGuideDoc />} />
        <Route path="/guide/center-manager" element={<CenterManagerGuideDoc />} />
        <Route path="/guide/coach-guide" element={<CoachGuideDoc />} />
        <Route path="/guide/parent-guide" element={<ParentGuideDoc />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <EnhancedToastProvider>
            <OfflineIndicator />
            <InstallPrompt />
            <AppContent />
          </EnhancedToastProvider>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const { loading, authInitialized, authRetrying, authError, usingCachedData } = useAuth();

  // Only show loading screen if we're not using cached data and auth is not ready
  // This allows instant loading with cached data while background refresh happens
  const shouldShowLoading = !authInitialized || (loading && !usingCachedData) || (authRetrying && !usingCachedData) || (authError && !usingCachedData);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {shouldShowLoading ? (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors duration-300">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-300">Loading...</p>
          </div>
        </div>
      ) : (
        <Suspense fallback={
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mx-auto"></div>
          </div>
        }>
          <AppRoutes />
        </Suspense>
      )}
    </div>
  );
}

export default App;
